<template>
    <AuthenticatorDialog
        :show="showAuthenticatorDialog"
        :authenticators="authenticators"
        @on-authenticator-selected="onLogin"
    />
    <div v-if="!initialized && !errorMessage" class="fixed-center">
        <q-spinner-ios size="2em" class="col" />
    </div>
    <ErrorMessage v-else-if="errorMessage" :error-message="errorMessage" />
    <Shell v-else-if="authenticated" :authenticated="authenticated" />
</template>

<script setup lang="ts">
import Shell from "@/shell/pages/shell.vue";
import {
    forceLogin,
    getAnonymousAuthenticator,
    getAuthenticators,
    initAuthentication,
    isAnonymousMode,
    isMixedMode,
    resolveSafeRedirectPath,
    tryGetToken,
} from "@/shared/authentication/authentication";
import { computed, onMounted, ref, watchEffect } from "vue";
import { Authenticator } from "@/shared/services/graphql/generated/public-graph-types";
import AuthenticatorDialog from "@/shared/authentication/components/authenticator-dialog.vue";
import { useTheme } from "@/shell/helpers/theme-definition";
import { setCssVar, useQuasar } from "quasar";
import { initAbilities } from "@/app/app.ts";
import { useTenant } from "@/shared/composables/tenant";
import ErrorMessage from "@/shared/components/placeholder/error-message.vue";
import { isMaintenance } from "@/shared/services/apollo-clients/links/error-link";
import { router } from "@/app/router.ts";
import { useEventListener } from "@vueuse/core";
import { useLanguageSync } from "@/shared/composables/useLanguageSync.ts";

const CALLBACK_URL = "callback";

const { screen } = useQuasar();
const { tenant, error } = useTenant();
const { colors, themeDefinitions, receiveMessage } = useTheme();

const authenticators = ref<Authenticator[]>([]);
const showAuthenticatorDialog = ref(false);
const initialized = ref(false);
const authenticated = ref(false);
const loginError = ref<string>();

function camelCaseToHyphen(str: string) {
    return str.replace(/[A-Z]/g, (match) => `-${match.toLowerCase()}`);
}

useEventListener("message", receiveMessage);

watchEffect(() => {
    if (!colors.value) return;

    for (const [key, value] of Object.entries(colors.value)) {
        setCssVar(`${camelCaseToHyphen(key)}`, value);
    }
    setCssVar("secondary", "#F2F2F2"); // $grey-95
    setCssVar("warning-bg", "#FFF1C9");
});

/* Sync i18n + Quasar whenever uiLocale changes */
useLanguageSync();

onMounted(() => {
    screen.setSizes({ sm: 628, md: 960, lg: 1280 });
    //TODO Refactoring
    /*  Ideally, we should start the Vue app as soon as all necessary data (login, theming, ...) are available.*/
});

const errorMessage = computed(() => {
    if (isMaintenance.value) return "Tenant is in maintenance mode";
    return loginError.value || error.value?.message;
});

const redirectAfterLoginUrl = computed(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(CALLBACK_URL) ?? undefined;
});

const called = watchEffect(async () => {
    if (!tenant.value || !themeDefinitions.value) return;

    try {
        called();
        /*
        We need to use window.location instead of router from vue router to get the initial full path.
        Router does not return the initial url but the redirect url in certain situations.
        */
        const initPath = window.location.href.replace(window.location.origin, "");

        initAuthentication(tenant.value?.authConfig);
        const successfulLogin = await tryGetToken();

        if (successfulLogin) {
            authenticated.value = true;

            //Ability initializing
            const { defaultPath } = await initAbilities(router, tenant.value);
            const callbackPath = resolveSafeRedirectPath(redirectAfterLoginUrl.value);
            const newRoutePath = callbackPath ?? (initPath === "/" ? defaultPath : initPath);
            await router.replace(newRoutePath);

            return;
        }

        if (isMixedMode() || isAnonymousMode()) {
            forceLogin(getAnonymousAuthenticator(), redirectAfterLoginUrl.value);
        } else {
            authenticators.value = getAuthenticators();
            if (authenticators.value.length === 1) {
                forceLogin(authenticators.value[0], redirectAfterLoginUrl.value);
            } else {
                showAuthenticatorDialog.value = true;
            }
        }
    } catch (e: unknown) {
        authenticated.value = false;
        loginError.value = e instanceof Error ? e.message : String(e);
        console.error(loginError.value);
    } finally {
        initialized.value = true;
    }
});

const onLogin = (authenticatorEdge: Authenticator) => {
    forceLogin(authenticatorEdge, redirectAfterLoginUrl.value);
};
</script>

<style lang="scss">
html {
    font-size: 14px;
}

html,
body {
    margin: 0;
    min-height: 100%;
    height: 100%;
    font-family: $base-font-family;
}
</style>
